import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteOfferingChildrenApi,
  getAllOfferingChildrenApi,
  getOfferingChildrenApi,
  postOfferingChildrenApi,
  updateOfferingChildrenApi,
} from "../../api/ecommerce/OfferingChildrenApi";

const initialState = {
  offeringChild: null,
  offeringChildren: [],
  loading: false,
  detailsLoading: false,
  saving: false,
  deleting: false,
  error: null,
  success: false,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const postOfferingChildren = createAsyncThunk(
  "offeringChildren/postOfferingChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postOfferingChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const updateOfferingChildren = createAsyncThunk(
  "offeringChildren/updateOfferingChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateOfferingChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteOfferingChildren = createAsyncThunk(
  "offeringChildren/deleteOfferingChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteOfferingChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getOfferingChildren = createAsyncThunk(
  "offeringChildren/getOfferingChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getOfferingChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getAllOfferingChildren = createAsyncThunk(
  "offeringChildren/getAllOfferingChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getAllOfferingChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const offeringChildrenSlice = createSlice({
  name: "offeringChildren",
  initialState,
  reducers: {
    clearOfferingChildrenError: (state) => {
      state.error = null;
    },
    clearOfferingChildrenState: (state) => {
      state.offeringChild = null;
      state.offeringChildren = [];
      state.loading = false;
      state.detailsLoading = false;
      state.saving = false;
      state.deleting = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOfferingChildren.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getOfferingChildren.fulfilled, (state, action) => {
        state.offeringChild = action?.payload?.response ?? action?.payload;
        state.detailsLoading = false;
        state.success = true;
      })
      .addCase(getOfferingChildren.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(getAllOfferingChildren.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAllOfferingChildren.fulfilled, (state, action) => {
        state.offeringChildren = action?.payload?.response ?? action?.payload ?? [];
        state.loading = false;
        state.success = true;
      })
      .addCase(getAllOfferingChildren.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postOfferingChildren.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postOfferingChildren.fulfilled, (state, action) => {
        state.offeringChild = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(postOfferingChildren.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(updateOfferingChildren.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateOfferingChildren.fulfilled, (state, action) => {
        state.offeringChild = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(updateOfferingChildren.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteOfferingChildren.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteOfferingChildren.fulfilled, (state) => {
        state.deleting = false;
        state.success = true;
      })
      .addCase(deleteOfferingChildren.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      });
  },
});

export const { clearOfferingChildrenError, clearOfferingChildrenState } =
  offeringChildrenSlice.actions;

export default offeringChildrenSlice.reducer;
